package fr.frux.feature.blockedusers

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.VisibilityOff
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.SnackbarHost
import androidx.compose.material3.SnackbarHostState
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.frux.core.designsystem.components.EmptyState
import fr.frux.core.designsystem.components.ErrorState
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.FruxTopBar
import fr.frux.core.designsystem.components.InitialsAvatar
import fr.frux.core.designsystem.components.LoadingState
import fr.frux.core.designsystem.components.SnackbarMessageEffect
import fr.frux.core.designsystem.components.message
import fr.frux.core.designsystem.error.userMessage
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.feature.blockedusers.resources.Res
import fr.frux.feature.blockedusers.resources.blockedusers_empty_message
import fr.frux.feature.blockedusers.resources.blockedusers_empty_title
import fr.frux.feature.blockedusers.resources.blockedusers_intro
import fr.frux.feature.blockedusers.resources.blockedusers_title
import fr.frux.feature.blockedusers.resources.blockedusers_unblock
import org.jetbrains.compose.resources.stringResource

/** « Comptes masqués » (#515, ADR 0029), sous-page du Profil. */
@Composable
fun BlockedUsersScreen(
    onBack: () -> Unit,
    viewModel: BlockedUsersViewModel = rememberBlockedUsersViewModel(),
) {
    val state by viewModel.state.collectAsStateWithLifecycle()
    val snackbarHostState = remember { SnackbarHostState() }
    SnackbarMessageEffect(
        trigger = state.message,
        message = state.message?.message(),
        snackbarHostState = snackbarHostState,
    ) { viewModel.consumeMessage() }

    Scaffold(
        topBar = { FruxTopBar(title = stringResource(Res.string.blockedusers_title), onBack = onBack) },
        snackbarHost = { SnackbarHost(snackbarHostState) },
        containerColor = MaterialTheme.colorScheme.background,
    ) { padding ->
        when {
            state.isLoading -> {
                LoadingState(Modifier.padding(padding))
            }

            state.error != null -> {
                ErrorState(
                    message = state.error!!.userMessage(),
                    onRetry = viewModel::retry,
                    modifier = Modifier.padding(padding),
                )
            }

            state.users.isEmpty() -> {
                EmptyState(
                    icon = Icons.Outlined.VisibilityOff,
                    title = stringResource(Res.string.blockedusers_empty_title),
                    message = stringResource(Res.string.blockedusers_empty_message),
                    modifier = Modifier.padding(padding),
                )
            }

            else -> {
                LazyColumn(
                    modifier = Modifier.fillMaxSize().padding(padding),
                    verticalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
                ) {
                    item {
                        Text(
                            text = stringResource(Res.string.blockedusers_intro),
                            style = MaterialTheme.typography.bodyMedium,
                            color = MaterialTheme.colorScheme.onSurfaceVariant,
                            modifier = Modifier.padding(horizontal = FruxSpacing.md, vertical = FruxSpacing.sm),
                        )
                    }
                    items(state.users, key = { it.userId }) { user ->
                        Row(
                            modifier = Modifier.fillMaxWidth().padding(horizontal = FruxSpacing.md),
                            verticalAlignment = Alignment.CenterVertically,
                            horizontalArrangement = Arrangement.spacedBy(FruxSpacing.md),
                        ) {
                            InitialsAvatar(username = user.username, size = 40.dp)
                            Text(
                                text = user.username,
                                style = MaterialTheme.typography.bodyLarge,
                                modifier = Modifier.weight(1f),
                            )
                            FruxButton(
                                text = stringResource(Res.string.blockedusers_unblock),
                                onClick = { viewModel.onUnblock(user) },
                                style = FruxButtonStyle.Outline,
                                loading = user.userId in state.pendingUserIds,
                            )
                        }
                    }
                }
            }
        }
    }
}
